import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  Card,
  TextField,
  IconButton,
  MenuItem,
  Divider,
  Switch,
  FormControlLabel,
  Tooltip,
  Grid,
} from "@mui/material";
import {
  ArrowBackIosNew as BackIcon,
  AddCircleOutline as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  InfoOutlined as InfoIcon,
  Visibility as PreviewIcon,
} from "@mui/icons-material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ModalShell from "components/ModalShell";

import { getForm, createForm, updateForm } from "api/endpoints/forms";
import { listEmailTemplates } from "api/endpoints/emailTemplates";

const FIELD_TYPES = ["text", "email", "number", "textarea", "select", "radio", "checkbox", "date"];

const FormBuilder = () => {
  const { formSlug } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [websiteId, setWebsiteId] = useState("");
  const [slug, setSlug] = useState(formSlug || "");
  const [fields, setFields] = useState([]);
  const [websiteName, setWebsiteName] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [formId, setFormId] = useState("");

  // Email template states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [adminTemplate, setAdminTemplate] = useState("");
  const [userTemplate, setUserTemplate] = useState("");
  const [adminTemplateEnabled, setAdminTemplateEnabled] = useState(true);
  const [userTemplateEnabled, setUserTemplateEnabled] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  const state = location.state || {};
  const queryWebsiteId = state.websiteId || "";
  const queryWebsiteName = state.websiteName || "";
  const queryFormId = state.formId || "";

  // Fetch email templates
  useEffect(() => {
    if (!queryWebsiteId) return;
    const fetchTemplates = async () => {
      try {
        const { items } = await listEmailTemplates(queryWebsiteId, { limit: 20 });
        setEmailTemplates(items);
      } catch (error) {
        // email templates are optional here — ignore a load failure
      }
    };

    fetchTemplates();
  }, [queryWebsiteId]);

  // Fetch form data in edit mode
  useEffect(() => {
    const fetchFormData = async () => {
      // If no formId, it’s a new form
      if (!queryFormId) {
        if (queryWebsiteId) {
          setWebsiteId(queryWebsiteId);
          setWebsiteName(queryWebsiteName);
        }
        return;
      }

      try {
        const formData = await getForm(queryWebsiteId, queryFormId);

        setFormId(formData.form_id); // store formId for update calls

        const emailNotifications = formData?.settings?.submission?.email_notification || [];
        const adminNotification = emailNotifications.find(
          (n) => n.to_addresses && n.to_addresses[0] !== "user"
        );
        const userNotification = emailNotifications.find(
          (n) => n.to_addresses && n.to_addresses[0] === "user"
        );

        setFormTitle(formData.name || "");
        setFormDescription(formData.description || "");
        setSlug(formData.slug || "");
        setWebsiteId(formData.website_id || queryWebsiteId || "");
        setWebsiteName(formData.website_name || queryWebsiteName || "");

        const loadedFields = formData.schema?.fields || [];
        if (Array.isArray(loadedFields)) {
          const formattedFields = loadedFields.map((f) => ({
            id: Date.now() + Math.random(),
            label: f.label || "",
            type: f.type || "text",
            placeholder: f.placeholder || "",
            help_text: f.help_text || "",
            required: f.required || false,
            validation: {
              min_length: f.validation?.min_length || "",
              max_length: f.validation?.max_length || "",
              pattern: f.validation?.pattern || "",
            },
            options: f.options && Array.isArray(f.options) ? f.options.join(", ") : f.options || "",
          }));

          setFields(formattedFields);
        }

        setNotificationEmail(adminNotification?.to_addresses?.[0] || "");
        setAdminTemplate(adminNotification?.template || "");
        setAdminTemplateEnabled(!!adminNotification?.enabled);
        setUserTemplate(userNotification?.template || "");
        setUserTemplateEnabled(!!userNotification?.enabled);
      } catch (error) {
        enqueueSnackbar("Failed to load form data. Please try again.", { variant: "error" });
      }
    };

    fetchFormData();
  }, [queryFormId, queryWebsiteId, queryWebsiteName]);

  const goBack = () => navigate(`/websites/${websiteName}`, { state: { websiteId, websiteName } });

  const addField = () => {
    setFields([
      ...fields,
      {
        id: Date.now(),
        label: "",
        type: "text",
        placeholder: "",
        help_text: "",
        required: false,
        validation: { min_length: "", max_length: "", pattern: "" },
        options: "",
      },
    ]);
  };

  const updateField = (id, key, value) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, [key]: value } : f)));
  };

  const updateValidation = (id, key, value) => {
    setFields(
      fields.map((f) => (f.id === id ? { ...f, validation: { ...f.validation, [key]: value } } : f))
    );
  };

  const deleteField = (id) => {
    setFields(fields.filter((f) => f.id !== id));
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    setFields(newFields);
  };

  const handleSave = async () => {
    if (!formTitle.trim() || !websiteId.trim() || !slug.trim()) {
      enqueueSnackbar("Please fill out all required fields: Title and Slug.", {
        variant: "warning",
      });
      return;
    }

    const fieldsToSend = fields.map((field) => {
      const normalizedName = field.label
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");

      return {
        id: normalizedName,
        name: normalizedName,
        label: field.label,
        type: field.type,
        placeholder: field.placeholder,
        help_text: field.help_text,
        required: field.required,
        validation: {
          ...(field.validation.min_length && { min_length: Number(field.validation.min_length) }),
          ...(field.validation.max_length && { max_length: Number(field.validation.max_length) }),
          ...(field.validation.pattern && { pattern: field.validation.pattern }),
        },
        ...(field.type === "select" || field.type === "radio" || field.type === "checkbox"
          ? {
              options: field.options
                .split(",")
                .map((opt) => opt.trim())
                .filter(Boolean),
            }
          : {}),
      };
    });

    const schemaObject = { fields: fieldsToSend };

    const settingsObject = {
      submission: {
        action: `/api/public/forms/${slug}/submission`,
        method: "POST",
        redirect_url: `/thank-you?form=${slug}`,
        email_notification: [
          {
            enabled: adminTemplateEnabled && !!notificationEmail,
            template: adminTemplate || "",
            to_addresses: notificationEmail ? [notificationEmail] : [],
          },
          {
            enabled: userTemplateEnabled,
            template: userTemplate || "",
            to_addresses: ["user"],
          },
        ],
      },
      version: "1.0",
    };

    const payload = formId
      ? {
          id: formId,
          active: true,
          name: formTitle,
          description: formDescription,
          slug,
          schema: schemaObject,
          settings: settingsObject,
        }
      : {
          name: formTitle,
          description: formDescription,
          slug,
          website_id: websiteId,
          schema: schemaObject,
          settings: settingsObject,
        };

    try {
      if (formId) {
        await updateForm(websiteId, formId, payload);
        enqueueSnackbar("Form updated successfully", { variant: "success" });
      } else {
        await createForm(websiteId, payload);
        enqueueSnackbar("Form created successfully", { variant: "success" });
      }
      goBack();
    } catch (error) {
      enqueueSnackbar(
        `Failed to save the form: ${error.response?.data?.message || "Please try again."}`,
        { variant: "error" }
      );
    }
  };

  return (
    <MDBox p={3}>
      <MDBox mb={2}>
        <MDButton variant="text" color="info" startIcon={<BackIcon />} onClick={goBack}>
          Back
        </MDButton>
      </MDBox>

      <Card sx={{ p: 4, maxWidth: 900, mx: "auto", borderRadius: 3 }}>
        <MDTypography variant="h4" fontWeight="bold" mb={3} textAlign="center">
          {formId ? "Edit Form" : "Create New Form"}
        </MDTypography>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Website Name" value={websiteName} disabled />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="Unique form identifier (e.g., contact-us)"
            />
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Form Title"
          value={formTitle}
          onChange={(e) => setFormTitle(e.target.value)}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label="Form Description"
          value={formDescription}
          onChange={(e) => setFormDescription(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          label="Notification Email (optional)"
          value={notificationEmail}
          onChange={(e) => setNotificationEmail(e.target.value)}
          placeholder="Enter email address to receive submissions"
          sx={{ mb: 3 }}
        />

        {/* Template Selection */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={adminTemplateEnabled}
                  onChange={(e) => setAdminTemplateEnabled(e.target.checked)}
                />
              }
              label="Enable Admin Template"
              sx={{ mb: 1 }}
            />
            <TextField
              select
              fullWidth
              label="Admin Email Template"
              value={adminTemplate}
              onChange={(e) => setAdminTemplate(e.target.value)}
              disabled={!adminTemplateEnabled}
              InputProps={{ sx: { height: 44 } }}
            >
              {emailTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={userTemplateEnabled}
                  onChange={(e) => setUserTemplateEnabled(e.target.checked)}
                />
              }
              label="Enable User Template"
              sx={{ mb: 1 }}
            />
            <TextField
              select
              fullWidth
              label="User Email Template"
              value={userTemplate}
              onChange={(e) => setUserTemplate(e.target.value)}
              disabled={!userTemplateEnabled}
              InputProps={{ sx: { height: 44 } }}
            >
              {emailTemplates.map((template) => (
                <MenuItem key={template.id} value={template.id}>
                  {template.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <MDTypography variant="h6" fontWeight="bold" mb={2}>
          Form Fields
        </MDTypography>

        {fields.map((field, index) => (
          <Card key={field.id} variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDTypography variant="subtitle2" fontWeight="bold">
                Field {index + 1}
              </MDTypography>
              <MDBox>
                <IconButton onClick={() => moveField(index, "up")} size="small" color="info">
                  <ArrowUpIcon />
                </IconButton>
                <IconButton onClick={() => moveField(index, "down")} size="small" color="info">
                  <ArrowDownIcon />
                </IconButton>
                <IconButton color="error" onClick={() => deleteField(field.id)} size="small">
                  <DeleteIcon />
                </IconButton>
              </MDBox>
            </MDBox>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Label"
                  value={field.label}
                  onChange={(e) => updateField(field.id, "label", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Type"
                  value={field.type}
                  onChange={(e) => updateField(field.id, "type", e.target.value)}
                  InputProps={{ sx: { height: 44 } }}
                >
                  {FIELD_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Placeholder"
                  value={field.placeholder}
                  onChange={(e) => updateField(field.id, "placeholder", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Help Text"
                  value={field.help_text}
                  onChange={(e) => updateField(field.id, "help_text", e.target.value)}
                />
              </Grid>

              {(field.type === "select" || field.type === "radio" || field.type === "checkbox") && (
                <Grid item xs={12}>
                  <TextField
                    label="Options (comma-separated)"
                    fullWidth
                    value={field.options}
                    onChange={(e) => updateField(field.id, "options", e.target.value)}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Min Length"
                  type="number"
                  fullWidth
                  value={field.validation.min_length}
                  onChange={(e) => updateValidation(field.id, "min_length", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Max Length"
                  type="number"
                  fullWidth
                  value={field.validation.max_length}
                  onChange={(e) => updateValidation(field.id, "max_length", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Tooltip title="Use regex for advanced validation">
                  <TextField
                    label="Pattern"
                    fullWidth
                    value={field.validation.pattern}
                    onChange={(e) => updateValidation(field.id, "pattern", e.target.value)}
                    InputProps={{
                      endAdornment: <InfoIcon fontSize="small" color="action" />,
                    }}
                  />
                </Tooltip>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.required}
                      onChange={(e) => updateField(field.id, "required", e.target.checked)}
                    />
                  }
                  label="Required Field"
                />
              </Grid>
            </Grid>
          </Card>
        ))}

        <MDButton
          variant="outlined"
          color="info"
          startIcon={<AddIcon />}
          onClick={addField}
          sx={{ mb: 3 }}
        >
          Add New Field
        </MDButton>

        <Divider sx={{ mb: 3 }} />

        <MDBox display="flex" justifyContent="space-between">
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewOpen(true)}
          >
            Preview
          </MDButton>

          <MDButton variant="gradient" color="info" onClick={handleSave}>
            {formId ? "Update Form" : "Save Form"}
          </MDButton>
        </MDBox>
      </Card>

      {/* Preview Modal */}
      <ModalShell open={previewOpen} onClose={() => setPreviewOpen(false)} title="Form Preview">
        <MDTypography variant="h6" fontWeight="bold" gutterBottom>
          {formTitle || "Untitled Form"}
        </MDTypography>
        <MDTypography variant="body2" color="text" mb={2}>
          {formDescription}
        </MDTypography>

        {fields.length === 0 && (
          <MDTypography variant="body2" color="text">
            No fields added yet.
          </MDTypography>
        )}

        {fields.map((field) => (
          <MDBox key={field.id} mb={2}>
            <MDTypography variant="body2" mb={0.5}>
              {field.label} {field.required && "*"}
            </MDTypography>

            {field.type === "textarea" ? (
              <TextField fullWidth multiline rows={3} placeholder={field.placeholder} />
            ) : field.type === "select" ? (
              <TextField select fullWidth placeholder={field.placeholder}>
                {(field.options?.split(",") || []).map((opt) => (
                  <MenuItem key={opt.trim()} value={opt.trim()}>
                    {opt.trim()}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField type={field.type} fullWidth placeholder={field.placeholder} />
            )}
            <MDTypography variant="caption" color="text">
              {field.help_text}
            </MDTypography>
          </MDBox>
        ))}
      </ModalShell>
    </MDBox>
  );
};

FormBuilder.propTypes = {
  initialData: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
        type: PropTypes.string,
        placeholder: PropTypes.string,
        help_text: PropTypes.string,
        required: PropTypes.bool,
        options: PropTypes.string,
        validation: PropTypes.shape({
          min_length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          max_length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          pattern: PropTypes.string,
        }),
      })
    ),
  }),
};

export default FormBuilder;
